import React from "react";

import { Button, Picto, Spinner, Title } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useTasksFilters } from "@/hooks/useTasksFilters";

import { StickyHeader } from "@/components/molecules/StickyHeader/StickyHeader";
import { SelectedTasksActionsMenu } from "@/components/organisms";

import { cn } from "@/utils/cn";

export interface ITasksListHeaderProps {
	title: string | React.ReactNode;
	count?: number;
	scroller?: string | HTMLElement;
	className?: string;
	isLoading?: boolean;
	/** The project (or view) is archived: nothing is written into it. */
	isArchived?: boolean;
	/** The caller only reads it (a VIEWER share). */
	isReadOnly?: boolean;
	scrollTrigger?: {
		target: string;
		start: string;
		end: string;
	};
	onMenuClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const TasksListHeader: React.FC<ITasksListHeaderProps> = ({
	title,
	count,
	isLoading,
	isArchived,
	isReadOnly,
	onMenuClick,
	className,
	scroller,
}) => {
	const { filters, setFilter } = useTasksFilters();

	return (
		<StickyHeader
			className={className}
			scroller={scroller}
			scrollTrigger={{
				start: "top-=100",
				end: "+=100",
				paddingTop: 0,
			}}
		>
			<div className="relative flex flex-wrap items-center justify-between">
				<div
					className={cn([
						isLoading && "row items-center gap-4",
						"overflow-hidden",
					])}
					onContextMenu={(e) => {
						if (e.altKey) return;
						e.preventDefault();
						onMenuClick?.(
							e as unknown as React.MouseEvent<HTMLButtonElement>
						);
					}}
				>
					<div className="row items-center gap-2">
						{onMenuClick && (
							<button
								className="flex items-center"
								onClick={onMenuClick}
							>
								<Picto
									icon={"more"}
									className="h-5 w-5 rotate-90 text-neutral-500"
								/>
							</button>
						)}
						<Title
							size="h3"
							className="overflow-hidden text-ellipsis capitalize"
						>
							{title}
						</Title>
						{isArchived ? (
							<span
								data-testid="archived-indicator"
								title={t("projects.infos.archived")}
								className="flex items-center"
							>
								<Picto
									icon="archive"
									className="h-4 min-w-4 text-neutral-500"
								/>
							</span>
						) : (
							isReadOnly && (
								<span
									data-testid="read-only-indicator"
									className="flex items-center"
								>
									<Picto
										icon="lock"
										className="h-4 min-w-4 text-neutral-500"
									/>
								</span>
							)
						)}
					</div>
					{isLoading ? (
						<Spinner size="sm" />
					) : (
						// No count when the listing failed or has not answered:
						// i18next cannot pick a plural form for undefined and
						// showed the raw key "tasks.count" instead.
						count !== undefined && (
							<div>
								<span className="text-sm text-neutral-500">
									{t("tasks.count", { count })}
								</span>
							</div>
						)
					)}
				</div>
				<div className="ml-auto flex items-center gap-4">
					<Button
						size="sm"
						color="white"
						className="flex items-center gap-2 text-black"
						onClick={() => {
							setFilter(
								"sortType",
								filters.sortType === "asc" ? "desc" : "asc"
							);
						}}
					>
						<Picto
							icon={
								filters.sortType === "asc"
									? "sortAsc"
									: "sortDesc"
							}
							className="h-4 w-4"
						/>
						{filters.sortType === "asc" ? "ASC" : "DESC"}
					</Button>
				</div>
			</div>
			<SelectedTasksActionsMenu />
		</StickyHeader>
	);
};
